<template>
  <div>
    <v-tabs
      v-model="mqttTab"
      class="mt-2"
    >
      <v-tab v-feature-key="{ key: 'mqtt.users.view', label: 'MQTT Users' }">
        Users
      </v-tab>
      <v-tab v-feature-key="{ key: 'mqtt.topics.manage', label: 'MQTT Topics' }">
        Topics
      </v-tab>
      <v-tab v-feature-key="{ key: 'mqtt.permissions.manage', label: 'MQTT Permissions' }">
        Permissions
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="mqttTab">
      <!-- Users -->
      <v-tab-item>
        <v-row
          class="mt-4"
          align="center"
        >
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <v-text-field
              v-model="userSearch"
              append-icon="mdi-magnify"
              label="Search users"
              clearable
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-select
              v-model="userFilter.isActive"
              :items="activeFilterItems"
              label="Status"
              clearable
              hide-details
              @change="reloadUsers"
            />
          </v-col>
          <v-col
            cols="12"
            md="5"
            class="text-md-right"
          >
            <app-btn
              class="mr-2"
              @click="loadUsers"
            >
              <v-icon>mdi-refresh</v-icon> Refresh
            </app-btn>
            <app-btn
              v-feature-key="{ key: 'mqtt.users.create', label: 'New MQTT User' }"
              @click="openUserDialog()"
            >
              <v-icon>mdi-plus</v-icon> New MQTT User
            </app-btn>
          </v-col>
        </v-row>

        <v-divider class="mt-3" />

        <v-data-table
          :headers="userHeaders"
          :items="userItems"
          :options.sync="userOptions"
          :server-items-length="totalUsers"
          :loading="usersLoading"
          class="responsive-data-table mqtt-data-table"
          item-key="id"
          :footer-props="{
            showFirstLastPage: true,
            'items-per-page-options': [10, 15, 25, 50],
          }"
        >
          <template v-slot:[`item.username`]="{ item }">
            <div class="font-weight-medium">
              {{ item.username || '-' }}
            </div>
            <div class="text-caption grey--text">
              {{ item.description || 'No description' }}
            </div>
          </template>

          <template v-slot:[`item.companyId`]="{ item }">
            {{ item.companyName || companyName(item.companyId) }}
          </template>

          <template v-slot:[`item.isActive`]="{ item }">
            <v-switch
              v-model="item.isActive"
              hide-details
              class="ma-0 pa-0"
              @change="updateUserStatus(item)"
            />
          </template>

          <template v-slot:[`item.createdAt`]="{ item }">
            {{ formatDateTime(item.createdAt) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="info"
                  v-bind="attrs"
                  v-on="on"
                  @click="showUserPermissions(item)"
                >
                  <v-icon small>
                    mdi-key-chain
                  </v-icon>
                </v-btn>
              </template>
              <span>Permissions</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="openUserDialog(item)"
                >
                  <v-icon small>
                    mdi-pencil
                  </v-icon>
                </v-btn>
              </template>
              <span>Edit user</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  @click="deleteUserDialog.show(item)"
                >
                  <v-icon small>
                    mdi-delete
                  </v-icon>
                </v-btn>
              </template>
              <span>Delete user</span>
            </v-tooltip>
          </template>
        </v-data-table>

        <user-prompt
          v-model="deleteUserDialog"
          @confirm="deleteUser"
        />
      </v-tab-item>

      <!-- Topics -->
      <v-tab-item>
        <v-row
          class="mt-4"
          align="center"
        >
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <v-text-field
              v-model="topicSearch"
              append-icon="mdi-magnify"
              label="Search topics"
              clearable
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-select
              v-model="topicFilter.isActive"
              :items="activeFilterItems"
              label="Status"
              clearable
              hide-details
              @change="reloadTopics"
            />
          </v-col>
          <v-col
            cols="12"
            md="5"
            class="text-md-right"
          >
            <app-btn
              class="mr-2"
              @click="loadTopics"
            >
              <v-icon>mdi-refresh</v-icon> Refresh
            </app-btn>
            <app-btn
              v-feature-key="{ key: 'mqtt.topics.manage', label: 'New MQTT Topic' }"
              @click="openTopicDialog()"
            >
              <v-icon>mdi-plus</v-icon> New Topic
            </app-btn>
          </v-col>
        </v-row>

        <v-divider class="mt-3" />

        <v-data-table
          :headers="topicHeaders"
          :items="topicItems"
          :options.sync="topicOptions"
          :server-items-length="totalTopics"
          :loading="topicsLoading"
          class="responsive-data-table mqtt-data-table"
          item-key="id"
          :footer-props="{
            showFirstLastPage: true,
            'items-per-page-options': [10, 15, 25, 50],
          }"
        >
          <template v-slot:[`item.topic`]="{ item }">
            <code class="mqtt-topic">{{ item.topic || '-' }}</code>
          </template>

          <template v-slot:[`item.isActive`]="{ item }">
            <v-chip
              small
              :color="item.isActive ? 'success' : 'grey'"
              text-color="white"
            >
              {{ item.isActive ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>

          <template v-slot:[`item.createdAt`]="{ item }">
            {{ formatDateTime(item.createdAt) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="info"
                  v-bind="attrs"
                  v-on="on"
                  @click="showTopicPermissions(item)"
                >
                  <v-icon small>
                    mdi-key-chain
                  </v-icon>
                </v-btn>
              </template>
              <span>Permissions</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="openTopicDialog(item)"
                >
                  <v-icon small>
                    mdi-pencil
                  </v-icon>
                </v-btn>
              </template>
              <span>Edit topic</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  @click="deleteTopicDialog.show(item)"
                >
                  <v-icon small>
                    mdi-delete
                  </v-icon>
                </v-btn>
              </template>
              <span>Delete topic</span>
            </v-tooltip>
          </template>
        </v-data-table>

        <user-prompt
          v-model="deleteTopicDialog"
          @confirm="deleteTopic"
        />
      </v-tab-item>

      <!-- Permissions -->
      <v-tab-item>
        <v-row
          class="mt-4"
          align="center"
        >
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-select
              v-model="permissionMode"
              :items="permissionModes"
              label="Browse by"
              hide-details
              @change="onPermissionModeChange"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <v-autocomplete
              v-if="permissionMode === 'user'"
              v-model="permissionUserId"
              :items="userLookup"
              item-text="username"
              item-value="id"
              label="MQTT User"
              clearable
              hide-details
              :loading="lookupLoading"
              @change="loadPermissions"
            />
            <v-autocomplete
              v-else
              v-model="permissionTopicId"
              :items="topicLookup"
              item-text="topic"
              item-value="id"
              label="Topic"
              clearable
              hide-details
              :loading="lookupLoading"
              @change="loadPermissions"
            />
          </v-col>
          <v-col
            cols="12"
            md="5"
            class="text-md-right"
          >
            <app-btn
              class="mr-2"
              @click="loadPermissions"
            >
              <v-icon>mdi-refresh</v-icon> Refresh
            </app-btn>
            <app-btn
              v-feature-key="{ key: 'mqtt.permissions.manage', label: 'New MQTT Permission' }"
              @click="openPermissionDialog()"
            >
              <v-icon>mdi-plus</v-icon> New Permission
            </app-btn>
          </v-col>
        </v-row>

        <v-divider class="mt-3" />

        <v-alert
          v-if="!permissionSelectionId"
          type="info"
          text
          dense
          class="mt-4"
        >
          Select {{ permissionMode === 'user' ? 'an MQTT user' : 'a topic' }} to view its permissions.
        </v-alert>

        <v-data-table
          v-else
          :headers="permissionHeaders"
          :items="permissionItems"
          :loading="permissionsLoading"
          class="responsive-data-table mqtt-data-table"
          item-key="id"
          :items-per-page="15"
          :footer-props="{
            showFirstLastPage: true,
            'items-per-page-options': [10, 15, 25, 50],
          }"
        >
          <template v-slot:[`item.username`]="{ item }">
            {{ item.username || userName(item.userId) }}
          </template>

          <template v-slot:[`item.topic`]="{ item }">
            <code class="mqtt-topic">{{ item.topic || topicName(item.topicId) }}</code>
          </template>

          <template v-slot:[`item.canPublish`]="{ item }">
            <v-switch
              v-model="item.canPublish"
              hide-details
              class="ma-0 pa-0"
              @change="savePermissionFlags(item)"
            />
          </template>

          <template v-slot:[`item.canSubscribe`]="{ item }">
            <v-switch
              v-model="item.canSubscribe"
              hide-details
              class="ma-0 pa-0"
              @change="savePermissionFlags(item)"
            />
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="openPermissionDialog(item)"
                >
                  <v-icon small>
                    mdi-pencil
                  </v-icon>
                </v-btn>
              </template>
              <span>Edit permission</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  @click="deletePermissionDialog.show(item)"
                >
                  <v-icon small>
                    mdi-delete
                  </v-icon>
                </v-btn>
              </template>
              <span>Delete permission</span>
            </v-tooltip>
          </template>
        </v-data-table>

        <user-prompt
          v-model="deletePermissionDialog"
          @confirm="deletePermission"
        />
      </v-tab-item>
    </v-tabs-items>

    <!-- User dialog -->
    <v-dialog
      v-model="userDialog"
      max-width="620px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ userItem.id ? 'Edit MQTT User' : 'New MQTT User' }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="userForm">
            <v-row class="mt-2">
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="userItem.username"
                  label="Username"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="userItem.password"
                  label="Password"
                  :type="showPassword ? 'text' : 'password'"
                  :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  :hint="userItem.id ? 'Leave blank to keep the current password' : ''"
                  persistent-hint
                  :rules="userItem.id ? [] : [rules.required]"
                  @click:append="showPassword = !showPassword"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  v-model="userItem.companyId"
                  :items="companies"
                  item-text="pathStr"
                  item-value="id"
                  label="Company (blank = global)"
                  clearable
                  :loading="companiesLoading"
                  @focus="$emit('load-companies')"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-switch
                  v-model="userItem.isActive"
                  label="Active"
                  hide-details
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="userItem.description"
                  label="Description"
                  rows="2"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            :disabled="savingUser"
            @click="userDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            :loading="savingUser"
            @click="saveUser"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Topic dialog -->
    <v-dialog
      v-model="topicDialog"
      max-width="620px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ topicItem.id ? 'Edit Topic' : 'New Topic' }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="topicForm">
            <v-row class="mt-2">
              <v-col
                cols="12"
                sm="8"
              >
                <v-text-field
                  v-model="topicItem.topic"
                  label="Topic"
                  placeholder="devices/+/telemetry"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-switch
                  v-model="topicItem.isActive"
                  label="Active"
                  hide-details
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="topicItem.description"
                  label="Description"
                  rows="2"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            :disabled="savingTopic"
            @click="topicDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            :loading="savingTopic"
            @click="saveTopic"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Permission dialog -->
    <v-dialog
      v-model="permissionDialog"
      max-width="620px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ permissionItem.id ? 'Edit Permission' : 'New Permission' }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="permissionForm">
            <v-row class="mt-2">
              <v-col
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  v-model="permissionItem.userId"
                  :items="userLookup"
                  item-text="username"
                  item-value="id"
                  label="MQTT User"
                  :loading="lookupLoading"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  v-model="permissionItem.topicId"
                  :items="topicLookup"
                  item-text="topic"
                  item-value="id"
                  label="Topic"
                  :loading="lookupLoading"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-switch
                  v-model="permissionItem.canPublish"
                  label="Can Publish"
                  hide-details
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-switch
                  v-model="permissionItem.canSubscribe"
                  label="Can Subscribe"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            :disabled="savingPermission"
            @click="permissionDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            :loading="savingPermission"
            @click="savePermission"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  export default {
    name: 'Esp32MqttServer',

    props: {
      companies: {
        type: Array,
        default: () => [],
      },
      companiesLoading: {
        type: Boolean,
        default: false,
      },
    },

    data: () => ({
      mqttTab: 0,
      rules: {
        required: (value) => (value !== null && value !== undefined && value !== '') || 'Required.',
      },
      activeFilterItems: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],

      usersLoading: false,
      userItems: [],
      totalUsers: 0,
      userOptions: {},
      userSearch: '',
      userSearchTimer: undefined,
      userFilter: {
        isActive: undefined,
      },
      userHeaders: [
        { text: 'Id', value: 'id' },
        { text: 'Username', value: 'username' },
        { text: 'Company', value: 'companyId' },
        { text: 'Active', value: 'isActive', sortable: false },
        { text: 'Created At', value: 'createdAt' },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false },
      ],
      userDialog: false,
      savingUser: false,
      showPassword: false,
      userItem: {},
      defaultUserItem: {
        id: undefined,
        username: '',
        password: '',
        isActive: true,
        description: '',
        companyId: undefined,
      },
      deleteUserDialog: null,

      topicsLoading: false,
      topicItems: [],
      totalTopics: 0,
      topicOptions: {},
      topicSearch: '',
      topicSearchTimer: undefined,
      topicFilter: {
        isActive: undefined,
      },
      topicHeaders: [
        { text: 'Id', value: 'id' },
        { text: 'Topic', value: 'topic' },
        { text: 'Description', value: 'description' },
        { text: 'Status', value: 'isActive' },
        { text: 'Created At', value: 'createdAt' },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false },
      ],
      topicDialog: false,
      savingTopic: false,
      topicItem: {},
      defaultTopicItem: {
        id: undefined,
        topic: '',
        description: '',
        isActive: true,
      },
      deleteTopicDialog: null,

      permissionMode: 'user',
      permissionModes: [
        { text: 'MQTT User', value: 'user' },
        { text: 'Topic', value: 'topic' },
      ],
      permissionUserId: undefined,
      permissionTopicId: undefined,
      permissionsLoading: false,
      permissionItems: [],
      permissionHeaders: [
        { text: 'Id', value: 'id' },
        { text: 'User', value: 'username' },
        { text: 'Topic', value: 'topic' },
        { text: 'Publish', value: 'canPublish', sortable: false },
        { text: 'Subscribe', value: 'canSubscribe', sortable: false },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false },
      ],
      permissionDialog: false,
      savingPermission: false,
      permissionItem: {},
      defaultPermissionItem: {
        id: undefined,
        userId: undefined,
        topicId: undefined,
        canPublish: true,
        canSubscribe: true,
      },
      deletePermissionDialog: null,

      lookupLoading: false,
      lookupLoaded: false,
      userLookup: [],
      topicLookup: [],
    }),

    computed: {
      permissionSelectionId () {
        return this.permissionMode === 'user' ? this.permissionUserId : this.permissionTopicId
      },
    },

    watch: {
      userOptions: {
        handler () {
          this.loadUsers()
        },
        deep: true,
      },

      topicOptions: {
        handler () {
          this.loadTopics()
        },
        deep: true,
      },

      userSearch () {
        clearTimeout(this.userSearchTimer)
        this.userSearchTimer = setTimeout(() => {
          this.reloadUsers()
        }, 500)
      },

      topicSearch () {
        clearTimeout(this.topicSearchTimer)
        this.topicSearchTimer = setTimeout(() => {
          this.reloadTopics()
        }, 500)
      },

      mqttTab (value) {
        if (value === 2) {
          this.ensureLookups()
        }
      },
    },

    created () {
      this.userItem = Object.assign({}, this.defaultUserItem)
      this.topicItem = Object.assign({}, this.defaultTopicItem)
      this.permissionItem = Object.assign({}, this.defaultPermissionItem)
    },

    methods: {
      notifyError (error, fallback) {
        this.$toast.error(
          error?.response?.data?.message || error?.message || fallback,
          'Error',
          { position: 'topRight' },
        )
      },

      notifySuccess (message) {
        this.$toast.success(message, 'Success', { position: 'topRight' })
      },

      companyName (companyId) {
        if (!companyId || Number(companyId) === -1) {
          return 'Global'
        }

        const company = this.companies.find(c => c.id === companyId)
        return company ? company.pathStr : companyId
      },

      userName (userId) {
        const user = this.userLookup.find(item => item.id === userId)
        return user ? user.username : (userId || '-')
      },

      topicName (topicId) {
        const topic = this.topicLookup.find(item => item.id === topicId)
        return topic ? topic.topic : (topicId || '-')
      },

      formatDateTime (value) {
        return value ? this.$format.dateTime(value) : '-'
      },

      buildListRequest (options, search, isActive) {
        return {
          pageNumber: Number(options?.page || 1),
          pageSize: Number(options?.itemsPerPage || 10),
          search: search || '',
          isActive: isActive === undefined || isActive === null ? null : isActive,
          sortColumn: options?.sortBy?.[0] || 'id',
          sortDirection: options?.sortDesc?.[0] ? 'desc' : 'asc',
        }
      },

      normalizeListResponse (responseData) {
        const wrapper = responseData || {}
        const source = Array.isArray(wrapper) ? wrapper : (wrapper.data ?? wrapper)

        if (Array.isArray(source)) {
          return { items: source, total: source.length }
        }

        const items = source?.items || source?.data || source?.results || source?.records || []
        const total = source?.totalCount ?? source?.total ?? source?.totalRecords ?? wrapper?.totalCount ?? wrapper?.total

        return {
          items: Array.isArray(items) ? items : [],
          total: Number(total ?? (Array.isArray(items) ? items.length : 0)),
        }
      },

      isFailedResponse (responseData) {
        return responseData?.success === false
      },

      // Users -------------------------------------------------------------
      reloadUsers () {
        if (!this.userOptions.page || this.userOptions.page === 1) {
          this.loadUsers()
          return
        }

        this.userOptions = { ...this.userOptions, page: 1 }
      },

      async loadUsers () {
        this.usersLoading = true
        try {
          const response = await this.$http.post(
            'mqtt/users/list',
            this.buildListRequest(this.userOptions, this.userSearch, this.userFilter.isActive),
          )
          const result = this.normalizeListResponse(response.data)

          this.userItems = result.items
          this.totalUsers = result.total
        } catch (error) {
          this.notifyError(error, 'Could not load MQTT users.')
        } finally {
          this.usersLoading = false
        }
      },

      openUserDialog (item) {
        this.userItem = Object.assign({}, this.defaultUserItem, item || {})
        this.userItem.password = ''
        this.showPassword = false
        this.userDialog = true
        if (this.$refs.userForm) {
          this.$refs.userForm.resetValidation()
        }
      },

      async saveUser () {
        if (!this.$refs.userForm.validate()) {
          return
        }

        const payload = {
          username: this.userItem.username,
          password: this.userItem.password || undefined,
          isActive: this.userItem.isActive,
          description: this.userItem.description || '',
          companyId: this.userItem.companyId || null,
        }

        this.savingUser = true
        try {
          const response = this.userItem.id
            ? await this.$http.put(`mqtt/users/${this.userItem.id}`, payload)
            : await this.$http.post('mqtt/users', payload)

          if (this.isFailedResponse(response.data)) {
            this.notifyError(null, response.data?.message || 'Could not save MQTT user.')
            return
          }

          this.notifySuccess(response.data?.message || 'MQTT user saved successfully.')
          this.userDialog = false
          this.lookupLoaded = false
          this.loadUsers()
        } catch (error) {
          this.notifyError(error, 'Could not save MQTT user.')
        } finally {
          this.savingUser = false
        }
      },

      async updateUserStatus (item) {
        try {
          const response = await this.$http.patch(`mqtt/users/${item.id}/status`, {
            isActive: item.isActive,
          })

          if (this.isFailedResponse(response.data)) {
            this.notifyError(null, response.data?.message || 'Could not update user status.')
            this.loadUsers()
            return
          }

          this.notifySuccess('MQTT user status updated.')
        } catch (error) {
          this.notifyError(error, 'Could not update user status.')
          this.loadUsers()
        }
      },

      async deleteUser (item) {
        try {
          const response = await this.$http.delete(`mqtt/users/${item.id}`)

          if (this.isFailedResponse(response.data)) {
            this.notifyError(null, response.data?.message || 'Could not delete MQTT user.')
            return
          }

          this.notifySuccess(response.data?.message || 'MQTT user deleted successfully.')
          this.lookupLoaded = false
          this.loadUsers()
        } catch (error) {
          this.notifyError(error, 'Could not delete MQTT user.')
        }
      },

      // Topics ------------------------------------------------------------
      reloadTopics () {
        if (!this.topicOptions.page || this.topicOptions.page === 1) {
          this.loadTopics()
          return
        }

        this.topicOptions = { ...this.topicOptions, page: 1 }
      },

      async loadTopics () {
        this.topicsLoading = true
        try {
          const response = await this.$http.post(
            'mqtt/topics/list',
            this.buildListRequest(this.topicOptions, this.topicSearch, this.topicFilter.isActive),
          )
          const result = this.normalizeListResponse(response.data)

          this.topicItems = result.items
          this.totalTopics = result.total
        } catch (error) {
          this.notifyError(error, 'Could not load MQTT topics.')
        } finally {
          this.topicsLoading = false
        }
      },

      openTopicDialog (item) {
        this.topicItem = Object.assign({}, this.defaultTopicItem, item || {})
        this.topicDialog = true
        if (this.$refs.topicForm) {
          this.$refs.topicForm.resetValidation()
        }
      },

      async saveTopic () {
        if (!this.$refs.topicForm.validate()) {
          return
        }

        const payload = {
          topic: this.topicItem.topic,
          description: this.topicItem.description || '',
          isActive: this.topicItem.isActive,
        }

        this.savingTopic = true
        try {
          const response = this.topicItem.id
            ? await this.$http.put(`mqtt/topics/${this.topicItem.id}`, payload)
            : await this.$http.post('mqtt/topics', payload)

          if (this.isFailedResponse(response.data)) {
            this.notifyError(null, response.data?.message || 'Could not save topic.')
            return
          }

          this.notifySuccess(response.data?.message || 'Topic saved successfully.')
          this.topicDialog = false
          this.lookupLoaded = false
          this.loadTopics()
        } catch (error) {
          this.notifyError(error, 'Could not save topic.')
        } finally {
          this.savingTopic = false
        }
      },

      async deleteTopic (item) {
        try {
          const response = await this.$http.delete(`mqtt/topics/${item.id}`)

          if (this.isFailedResponse(response.data)) {
            this.notifyError(null, response.data?.message || 'Could not delete topic.')
            return
          }

          this.notifySuccess(response.data?.message || 'Topic deleted successfully.')
          this.lookupLoaded = false
          this.loadTopics()
        } catch (error) {
          this.notifyError(error, 'Could not delete topic.')
        }
      },

      // Permissions -------------------------------------------------------
      async ensureLookups (force = false) {
        if (this.lookupLoading || (this.lookupLoaded && !force)) {
          return
        }

        this.lookupLoading = true
        try {
          const listRequest = {
            pageNumber: 1,
            pageSize: 500,
            search: '',
            isActive: null,
            sortColumn: 'id',
            sortDirection: 'asc',
          }
          const [usersResponse, topicsResponse] = await Promise.all([
            this.$http.post('mqtt/users/list', listRequest),
            this.$http.post('mqtt/topics/list', listRequest),
          ])

          this.userLookup = this.normalizeListResponse(usersResponse.data).items
          this.topicLookup = this.normalizeListResponse(topicsResponse.data).items
          this.lookupLoaded = true
        } catch (error) {
          this.notifyError(error, 'Could not load MQTT users and topics.')
        } finally {
          this.lookupLoading = false
        }
      },

      onPermissionModeChange () {
        this.permissionItems = []
        this.loadPermissions()
      },

      async loadPermissions () {
        if (!this.permissionSelectionId) {
          this.permissionItems = []
          return
        }

        this.permissionsLoading = true
        try {
          const url = this.permissionMode === 'user'
            ? `mqtt/permissions/by-user/${this.permissionUserId}`
            : `mqtt/permissions/by-topic/${this.permissionTopicId}`
          const response = await this.$http.get(url)
          const data = response.data?.data ?? response.data

          this.permissionItems = Array.isArray(data) ? data : (data?.items || [])
        } catch (error) {
          this.notifyError(error, 'Could not load MQTT permissions.')
        } finally {
          this.permissionsLoading = false
        }
      },

      async showUserPermissions (user) {
        await this.ensureLookups()
        this.permissionMode = 'user'
        this.permissionUserId = user.id
        this.mqttTab = 2
        this.loadPermissions()
      },

      async showTopicPermissions (topic) {
        await this.ensureLookups()
        this.permissionMode = 'topic'
        this.permissionTopicId = topic.id
        this.mqttTab = 2
        this.loadPermissions()
      },

      openPermissionDialog (item) {
        this.ensureLookups()
        this.permissionItem = Object.assign({}, this.defaultPermissionItem, item || {})

        if (!item) {
          this.permissionItem.userId = this.permissionMode === 'user' ? this.permissionUserId : undefined
          this.permissionItem.topicId = this.permissionMode === 'topic' ? this.permissionTopicId : undefined
        }

        this.permissionDialog = true
        if (this.$refs.permissionForm) {
          this.$refs.permissionForm.resetValidation()
        }
      },

      async savePermission () {
        if (!this.$refs.permissionForm.validate()) {
          return
        }

        this.savingPermission = true
        try {
          const saved = await this.submitPermission(this.permissionItem)

          if (!saved) {
            return
          }

          this.permissionDialog = false

          if (this.permissionMode === 'user' && !this.permissionUserId) {
            this.permissionUserId = this.permissionItem.userId
          }
          if (this.permissionMode === 'topic' && !this.permissionTopicId) {
            this.permissionTopicId = this.permissionItem.topicId
          }

          this.loadPermissions()
        } finally {
          this.savingPermission = false
        }
      },

      async savePermissionFlags (item) {
        const saved = await this.submitPermission(item)

        if (!saved) {
          this.loadPermissions()
        }
      },

      async submitPermission (item) {
        const payload = {
          userId: item.userId,
          topicId: item.topicId,
          canPublish: !!item.canPublish,
          canSubscribe: !!item.canSubscribe,
        }

        try {
          const response = item.id
            ? await this.$http.put(`mqtt/permissions/${item.id}`, payload)
            : await this.$http.post('mqtt/permissions', payload)

          if (this.isFailedResponse(response.data)) {
            this.notifyError(null, response.data?.message || 'Could not save permission.')
            return false
          }

          this.notifySuccess(response.data?.message || 'Permission saved successfully.')
          return true
        } catch (error) {
          this.notifyError(error, 'Could not save permission.')
          return false
        }
      },

      async deletePermission (item) {
        try {
          const response = await this.$http.delete(`mqtt/permissions/${item.id}`)

          if (this.isFailedResponse(response.data)) {
            this.notifyError(null, response.data?.message || 'Could not delete permission.')
            return
          }

          this.notifySuccess(response.data?.message || 'Permission deleted successfully.')
          this.loadPermissions()
        } catch (error) {
          this.notifyError(error, 'Could not delete permission.')
        }
      },
    },
  }
</script>

<style scoped>
  .mqtt-data-table {
    margin-top: 12px;
  }

  .mqtt-topic {
    background: #f5f5f5;
    border-radius: 4px;
    box-shadow: none;
    font-size: 12px;
    padding: 2px 6px;
  }
</style>
